import { lazy } from "react";
import Welcome from "./pages/Welcome";
import SubscriptionWarning from "./pages/SubscriptionWarning";

const DesignViewer = lazy(() => import("@Features/design-viewer/components/DesignViewer/DesignViewer"));
const Issues = lazy(() => import("@Features/issues/components/Issues/Issues"));
const IssuesList = lazy(() => import("@Features/issues/pages/Issues/IssuesList"));
const PhotosList = lazy(() => import("@Features/photos/pages/Photos/PhotosList"));
const FileExplorer = lazy(() => import("@Features/files/pages/FileExplorer"));
// Not lazy: it only redirects the legacy explorer URL to /project/:projectId/folders, so load it eagerly
// (no chunk fetch before the redirect fires).
import RedirectExplorerToFolders from "@Features/files/pages/FileExplorer/RedirectExplorerToFolders";
const MobileExplorer = lazy(() => import("@Features/files/pages/FileExplorer/Mobile.jsx"));
const TaskDetails = lazy(() => import("./pages/TaskManagement/TaskDetails/TaskDetails"));
const TransmitalsList = lazy(
  () => import("@Features/transmittals/pages/Transmitals/ListTransmittals/listTransmittals"),
);
const TransmitalDetails = lazy(
  () => import("@Features/transmittals/pages/Transmitals/TransmitalDetails/TransmitalDetails"),
);
const TaskList = lazy(() => import("./pages/TaskManagement/ListTask/listTask"));
// const Gantt = lazy(() => import("./components/Schedule/Gantt"));
const PortalDashboard = lazy(() => import("@Features/portal/dashboard"));
const ProjectDashboard = lazy(() =>
  import("@Features/projects").then((module) => ({ default: module.ProjectDashboard })),
);
const PortalSetting = lazy(() =>
  import("@Features/portal-settings").then((module) => ({ default: module.PortalSettingsPage })),
);
const UserProfile = lazy(() => import("./pages/UserProfile"));
const ProjectSettings = lazy(() =>
  import("@Features/projects").then((module) => ({ default: module.ProjectSettings })),
);
const TaskCreation = lazy(() => import("./pages/TaskManagement/TaskCreation"));
const NotificationsPage = lazy(() =>
  import("@Features/portal").then((module) => ({ default: module.NotificationsPage })),
);
const EscrowFiles = lazy(() => import("@Features/portal").then((module) => ({ default: module.EscrowFilesPage })));
const Workspaces = lazy(() => import("./pages/Workspaces"));
const ProjectsPage = lazy(() => import("@Features/projects").then((module) => ({ default: module.ProjectsPage })));
const LoginCallback = lazy(() => import("./components/LoginCallback/LoginCallback"));
const LinkingAccount = lazy(() => import("./components/LinkingAccount"));
const SignInPage = lazy(() => import("./pages/SignIn"));
const OrganisationSettings = lazy(() => import("./pages/OrganisationSettings/OrganisationSettings"));
const EstimateListPage = lazy(() =>
  import("@Features/estimation").then((module) => ({ default: module.EstimateListPage })),
);
const Estimate = lazy(() => import("@Features/estimation").then((module) => ({ default: module.EstimatePage })));
// const Diagram = lazy(() => import("./components/Diagram"));
const SuperAdmin = lazy(() => import("@Features/portal").then((module) => ({ default: module.SuperAdminPage })));
const Forbidden = lazy(() => import("./pages/Error/Forbidden"));
const ScheduleListPage = lazy(() => import("./pages/Schedules/ScheduleListPage"));
const ContractsPage = lazy(() => import("@Features/contracts/pages/Contracts"));
const ContractDetails = lazy(() => import("@Features/contracts/pages/Contracts/ContractDetails"));
// const ResourceTypeList = lazy(() => import("./components/Resource/ResourceType"));
const FormsList = lazy(() => import("@Features/forms/pages/Forms"));
// const ResourceList = lazy(() => import("./components/Resource"));
const GoogleSigninButton = lazy(() => import("./components/GoogleSigninButton"));
const ThankYou = lazy(() => import("./components/ThankYou"));
const ScheduleDetailsNew = lazy(() => import("./pages/Schedules/ScheduleDetailsNew"));
const CreateProject = lazy(() => import("@Features/projects").then((module) => ({ default: module.CreateNewProject })));
const ClashTests = lazy(() => import("./pages/ClashTests"));
const ClashTestDetails = lazy(() => import("./pages/ClashTests/ClashDetails"));
const ProjectFiles = lazy(() => import("@Features/files/pages/Files/index2"));
const AssetsList = lazy(() => import("./pages/Assets/index"));
const AssetRelatedDocuments = lazy(() => import("./pages/Assets/RelatedDocuments"));
const PortalFiles = lazy(() => import("@Features/portal/pages/Files/PortalFiles"));
const CreateInspectionAndProgres = lazy(() =>
  import("@Features/forms").then((module) => ({ default: module.CreateInspectionAndProgressPage })),
);
const EstimatePortalList = lazy(() =>
  import("@Features/estimation").then((module) => ({ default: module.EstimatePortalListPage })),
);
const ReviewPage = lazy(() => import("@Features/reviews/ReviewPage"));
const Mailbox = lazy(() => import("@Features/mailbox/pages/Mailbox/index"));
const Reviews = lazy(() => import("@Features/reviews/ReviewsPage"));
const MailThread = lazy(() => import("@Features/mailbox/pages/Mailbox/MailThread"));
const PdfGenerator = lazy(() => import("@Features/pdf-viewer/components/PdfGenerator/PdfGenerator"));
const AdminBilling = lazy(() => import("@Features/portal-settings/pages/AdminBilling/AdminBilling"));
const isSuperAdminUrl = false;
const SignupFlow = lazy(() => import("./components/SignupFlow/SignupFlow"));
const InviteUser = lazy(() => import("./components/SignupFlow/InviteUser"));
const ForgotPassword = lazy(() => import("./components/SignupFlow/ForgotPassword"));
const AiChat = lazy(() => import("./pages/AiChat"));
const SignInUser = lazy(() => import("./components/SignupFlow/SignInUser"));
// Recipient-facing shareable-link viewer (CP-283). Unauthenticated: rendered with no header and no
// sidebar, and excluded from the sign-in bounce in Router.tsx.
const SharedLinkPage = lazy(() => import("@Features/external-share/pages/SharedLinkPage"));
// The recipient's file viewer. Renders the real DesignViewer, so PDF/BIM/CAD/image/video/sheet all
// behave exactly as they do internally.
const SharedFileViewerPage = lazy(() => import("@Features/external-share/pages/SharedFileViewerPage"));

const routes = [
  {
    path: ["/resetPassword"],
    element: SignInPage,
    exact: true,
    showStickySidebar: false,
    showHeader: false,
  },
  {
    path: ["/googleSigninButton"],
    element: GoogleSigninButton,
    exact: true,
    showStickySidebar: false,
    showHeader: false,
  },
  {
    path: ["/thank-you"],
    element: ThankYou,
    exact: true,
    showStickySidebar: false,
    showHeader: false,
  },
  {
    path: ["/signup", "/95789"],
    element: SignInPage,
    props: { formType: "signup" },
    showStickySidebar: false,
    showHeader: false,
  },
  {
    path: ["/invitation"],
    element: SignInPage,
    props: { formType: "invitation" },
    showStickySidebar: false,
    showHeader: false,
  },
  {
    path: ["/user-profile/:settingType"],
    element: UserProfile,
    showStickySidebar: false,
  },
  {
    path: ["/dashboard"],
    element: isSuperAdminUrl ? SuperAdmin : PortalDashboard,
    showStickySidebar: false,
  },
  {
    path: ["/welcome"],
    element: Welcome,
    showStickySidebar: false,
    showHeader: false,
  },
  {
    path: ["/subscriptionWarning"],
    element: SubscriptionWarning,
    showStickySidebar: false,
    showHeader: false,
  },
  {
    path: ["/googleLoginCallback"],
    element: LoginCallback,
    showStickySidebar: false,
  },
  {
    path: ["/linkingAccount"],
    element: LinkingAccount,
    showStickySidebar: false,
    showHeader: false,
  },
  {
    path: ["/projects"],
    element: ProjectsPage,
    showStickySidebar: false,
  },
  { path: ["/403"], element: Forbidden, showStickySidebar: false },
  { path: ["/project/:projectId/mails"], element: Mailbox },
  { path: ["/reviews"], element: Reviews },
  {
    path: ["/project/:projectId/mails/:mailId/mailThread"],
    element: MailThread,
  },
  // Legacy explorer URL: redirect to the new project-level Folders URL (Space id moves into the `spaceId` query param).
  {
    path: ["/workspace/:workspaceId/explorer"],
    element: RedirectExplorerToFolders,
    showStickySidebar: false,
    showHeader: false,
  },
  { path: ["/project/:projectId/folders"], element: FileExplorer },
  {
    path: ["/workspace/:workspaceId/mobileExplorer"],
    element: MobileExplorer,
    showStickySidebar: false,
    showHeader: false,
  },
  { path: ["/Files"], element: PortalFiles },
  {
    path: ["/workspace/:workspaceId/file/:fileid/issues"],
    element: Issues,
  },
  { path: ["/project/:projectId/issues", "/issuesList"], element: IssuesList },
  { path: ["/project/:projectId/photos"], element: PhotosList },
  { path: ["/project/:projectId/setting"], element: ProjectSettings },
  {
    path: ["/project/:projectId/listTransmittals"],
    element: TransmitalsList,
    exact: true,
  },
  {
    path: ["/project/:projectId/listFiles"],
    element: ProjectFiles,
    exact: true,
  },
  {
    path: ["/project/:projectId/assetsList"],
    element: AssetsList,
    exact: true,
  },
  {
    path: ["/project/:projectId/assetsList/:assetId/relatedDocuments"],
    element: AssetRelatedDocuments,
    exact: true,
  },
  {
    path: ["/project/:projectId/EstimateList"],
    element: EstimateListPage,
  },
  {
    path: ["/project/:projectId/listTransmittals/:transmittalVersionId"],
    element: TransmitalDetails,
  },
  {
    path: ["/project/:projectId/estimate/:estimateId", "/project/:projectId/estimate/:estimateId/version/:versionId"],
    element: Estimate,
    exact: true,
  },
  { path: ["/transmittals"], element: TransmitalsList },
  {
    path: [
      "/project/:projectId/createTasks",
      "/project/:projectId/editTask/:taskId",
      "/project/:projectId/editTask/:taskId/schedule/:scheduleId",
      "/project/:projectId/schedule/:scheduleId/spaceId/:spaceId/createTasks",
      "/project/:projectId/schedule/:scheduleId/createTasks",
    ],
    element: TaskCreation,
  },
  {
    path: ["/project/:projectId/listTask"],
    element: TaskList,
    exact: true,
  },
  {
    path: ["/project/:projectId/forms/:resourceId"],
    element: CreateInspectionAndProgres,
    exact: true,
  },
  {
    path: ["/project/:projectId/listTask/:taskId"],
    element: TaskDetails,
  },
  { path: ["/tasks"], element: TaskList },
  {
    path: [
      "/portalSettings/:settingType",
      "/portalSettings/:settingType/:formId/:formCategory",
      "/portalSettings/:settingType/:costDatabaseId/costDatabaseVersion/:costDatabaseVersionId",
      "/portalSettings/:settingType/checklists/:checklistId",
      "/portalSettings/:settingType/:costDatabaseId",
      "/portalSettings/:settingType/:projectId/projectUsers",
      "/portalSettings/:settingType/:userId/userProjects",
      "/portalSettings/:settingType/:organisationId/orgUsers",
    ],
    element: PortalSetting,
    exact: true,
    showStickySidebar: false,
  },
  {
    path: ["/admin-billing"],
    element: AdminBilling,
    showStickySidebar: false,
  },
  {
    path: ["/organisationSettings/:organisationId/:settingType?"],
    element: OrganisationSettings,
    showStickySidebar: false,
  },
  {
    path: ["/project/:projectId/projectdashboard"],
    element: ProjectDashboard,
  },
  { path: ["/project/:projectId/Workspaces"], element: Workspaces },
  {
    path: ["/contractsList", "/project/:projectId/contractsList"],
    element: ContractsPage,
    exact: true,
  },
  {
    path: ["/project/:projectId/contract/:contractId"],
    element: ContractDetails,
    exact: true,
  },
  {
    path: ["/project/:projectId/formsList/:moduleName"],
    element: FormsList,
    exact: true,
  },
  /// not in use
  // {
  //   path: ["/project/:projectId/space/:spaceId/gantt"],
  //   element: Gantt,
  //   exact: true,
  // },
  {
    path: ["/project/:projectId/scheduleList"],
    element: ScheduleListPage,
    exact: true,
  },
  {
    path: [
      "/project/:projectId/schedule/:scheduleId/ScheduleDetails",
      "/project/:projectId/schedule/:scheduleId/version/:versionId",
    ],
    element: ScheduleDetailsNew,
    exact: true,
  },
  {
    path: ["/designviewer/file/:fileId"],
    element: DesignViewer,
    showHeader: false,
    showStickySidebar: false,
  },
  {
    path: ["/project/:projectId/clashTests"],
    element: ClashTests,
    exact: true,
  },
  {
    path: ["/project/:projectId/clashDetails/:clashTestId"],
    element: ClashTestDetails,
  },
  {
    path: ["/notifications", "/support"],
    element: NotificationsPage,
    showStickySidebar: false,
  },
  {
    path: ["/escrowFiles/:batchId"],
    element: EscrowFiles,
    showStickySidebar: false,
  },
  {
    path: ["/project/createNewProject"],
    element: CreateProject,
    exact: true,
  },
  {
    path: ["/estimatePortalList"],
    element: EstimatePortalList,
    exact: true,
    showStickySidebar: false,
  },
  { path: ["/review", "/reviews?byPortal=true"], element: ReviewPage, exact: true },
  {
    path: [":module/pdfExport"],
    element: PdfGenerator,
    showStickySidebar: false,
    showHeader: false,
  },
  {
    path: ["/", "/newSignIn"],
    element: SignInUser,
    exact: true,
    showStickySidebar: false,
    showHeader: false,
  },
  {
    path: ["/signupflow"],
    element: SignupFlow,
    showStickySidebar: false,
    showHeader: false,
  },
  {
    path: ["/newInvitation"],
    element: InviteUser,
    exact: true,
    showStickySidebar: false,
    showHeader: false,
  },
  {
    path: ["/reset-password"],
    element: ForgotPassword,
    exact: true,
    showStickySidebar: false,
    showHeader: false,
  },
  {
    // Declared BEFORE the bare token route: `matchRoute` builds a regex per path and takes the first
    // hit, and `/share/:token` would otherwise not match this deeper path anyway — but keeping the more
    // specific route first makes the intent explicit.
    //
    // The param MUST be named `:fileId`, matching `/designviewer/file/:fileId`: DesignViewer reads
    // `useParams().fileId` directly, so any other name here leaves it undefined. It was `:fileVersionId`
    // and that is exactly what broke — `setCurrentFileVersionId(undefined)` on a string-initialised
    // slice throws "A case reducer on a non-draftable value must not return undefined".
    path: ["/share/:token/file/:fileId"],
    element: SharedFileViewerPage,
    exact: true,
    showStickySidebar: false,
    showHeader: false,
  },
  {
    path: ["/share/:token"],
    element: SharedLinkPage,
    exact: true,
    showStickySidebar: false,
    showHeader: false,
  },
  {
    path: ["/ai/chat", "/ai/chat/:conversationId"],
    element: AiChat,
    showStickySidebar: false,
    showHeader: false,
  },
];

export default routes;
